import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiMinus } = FiIcons;

const FAQSection = () => {
  const [openFaq, setOpenFaq] = useState(0);

  const faqs = [
    {
      question: 'How does AI scoring work?',
      answer: 'Our AI analyzes video interviews using natural language processing to evaluate technical skills, communication clarity, and soft skills. It considers factors like keyword matching, response structure, and confidence indicators. All scoring is transparent and can be reviewed by your hiring team.'
    },
    {
      question: 'Can I override AI recommendations?',
      answer: 'Absolutely. AI provides insights and recommendations, but human judgment always has the final say. You can adjust scores, add manual notes, and make hiring decisions based on your specific requirements and company culture fit.'
    },
    {
      question: 'What is proxy detection and how does it work?',
      answer: 'Proxy detection uses advanced AI to verify that the person taking the interview is the actual candidate. It analyzes visual cues, voice patterns, and behavioral indicators to ensure interview authenticity and maintain hiring fairness.'
    },
    {
      question: 'How long does onboarding typically take?',
      answer: 'Most teams are up and running within 24-48 hours. Our onboarding includes account setup, team training, job template creation, and a guided first hire. Enterprise customers receive dedicated support and custom integration assistance.'
    },
    {
      question: 'What integrations do you support?',
      answer: 'We integrate with popular ATS systems (Workday, BambooHR, Greenhouse), communication tools (Slack, Teams), and can connect via API to custom systems. Enterprise plans include dedicated integration support.'
    },
    {
      question: 'Can candidates reattempt interviews?',
      answer: 'Yes, you can configure retry policies per job. Candidates can typically reattempt once with a 24-hour cooling period. All attempts are logged for transparency and fairness tracking.'
    }
  ];

  return (
    <section id="faq" className="py-16 lg:py-24 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="font-display font-bold text-3xl lg:text-5xl text-slate-900 mb-6">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-slate-600">
            Everything you need to know about Instashortlist
          </p>
        </motion.div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              className="bg-slate-50 rounded-2xl overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <button
                className="w-full px-8 py-6 text-left flex items-center justify-between hover:bg-slate-100 transition-colors"
                onClick={() => setOpenFaq(openFaq === index ? -1 : index)}
              >
                <span className="font-semibold text-lg text-slate-900 pr-4">
                  {faq.question}
                </span>
                <SafeIcon 
                  icon={openFaq === index ? FiMinus : FiPlus} 
                  className="w-6 h-6 text-slate-500 flex-shrink-0" 
                />
              </button>
              
              <AnimatePresence>
                {openFaq === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="px-8 pb-6">
                      <p className="text-slate-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <p className="text-slate-600 mb-4">
            Still have questions?
          </p>
          <button 
            onClick={() => window.open('mailto:support@instashortlist.com')}
            className="bg-indigo-600 text-white font-semibold px-8 py-3 rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Contact Support
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQSection;